// Typed wrappers for company contacts — the people behind a company
// (go-pim migration 0009).
//
// A contact is a SEPARATE SUB-RESOURCE, deliberately not a field on the company
// payload. PUT /v1/companies/:id is a full-row replace, and a nested collection
// under those semantics means any client that did not send `contacts` deletes
// every one of them. Its own routes give it its own verbs: absent means absent,
// and removing a person is an explicit DELETE rather than an omission from
// somebody else's body.
//
// THE THREE THINGS A READER GETS WRONG HERE:
//
//   1. role is CLOSED, designation is FREE TEXT — and designation is the one
//      that PRINTS. See the two blocks below; never build a designation picker.
//   2. print_on decides which document a contact reaches. role does NOT.
//      A director with print_on 'none' appears on nothing.
//   3. A printing contact is FROZEN into the document's issuer snapshot at issue
//      time, so editing or deleting one here never changes a document already
//      issued. Say so in the UI — an operator who believes a delete rewrites
//      history will avoid tidying stale rows, and one who believes an edit fixes
//      a misspelling on an issued receipt will be wrong the other way.
//
// All calls go through the shared apiClient (bearer + 401-refresh-retry).
// Scope is by company id IN THE PATH on the collection route; the item routes
// are flat (/v1/contacts/:id) and carry no company segment.
import { apiClient } from "./client";

/**
 * ContactRole — CLOSED. Validated on write (400 naming the allowed values) and
 * code branches on it, so it is exactly the kind of value CLAUDE.md's closed-enum
 * invariant covers: grouping, ordering, and any "who signs this" logic keys off
 * these tokens.
 *
 * It is NOT what gets printed. See Designation on the Contact type.
 */
export type ContactRole =
  | "authorised_signatory"
  | "primary_contact"
  | "billing"
  | "support"
  | "director"
  | "other";

/**
 * PrintOn — CLOSED, and enforced by a CHECK constraint in the migration as well
 * as in Go (unlike role, this set is tiny and genuinely fixed).
 *
 * This — not role — is what decides which document a contact reaches. go-pim's
 * render path selects on `print_on IN (<docType>, 'both') AND active` and never
 * looks at role: which lines a compliant document needs is answered by what the
 * operator filled in, not by a per-doc-type code branch.
 *
 * 'none' is the default because a contact is useful as a record even when it
 * prints nowhere, and because adding a person must never silently change what
 * customers receive.
 */
export type PrintOn = "none" | "receipt" | "invoice" | "both";

// ─── Human labels ─────────────────────────────────────────────────────────
//
// Operators must NEVER see the raw wire tokens. "authorised_signatory" in a
// dropdown is a leak of the database into the panel; every <select> option,
// pill and summary renders through these maps. The keys are the backend
// contract and are unchanged — only the presentation is humanised.

export const CONTACT_ROLE_LABELS: Record<ContactRole, string> = {
  authorised_signatory: "Authorised signatory",
  primary_contact: "Primary contact",
  billing: "Billing",
  support: "Support",
  director: "Director",
  other: "Other",
};

/**
 * Phrased as an ANSWER TO "where does this person appear?", not as a bare doc
 * type. "Invoice" alone next to a role field reads as "this person is about
 * invoices"; the operator needs to know the name is physically rendered on the
 * page a customer receives.
 */
export const PRINT_ON_LABELS: Record<PrintOn, string> = {
  none: "Don't print anywhere",
  receipt: "Print on receipts",
  invoice: "Print on invoices",
  both: "Print on receipts + invoices",
};

/**
 * Ordered option lists for the <select> controls.
 *
 * CONTACT_ROLES runs most-consequential first (a signatory is the reason this
 * table exists) and keeps "other" last, where a catch-all belongs. PRINT_ONS
 * starts at 'none' because that is the server default and the safe choice —
 * a new contact prints nowhere until somebody says otherwise.
 */
export const CONTACT_ROLES: ContactRole[] = [
  "authorised_signatory",
  "primary_contact",
  "billing",
  "support",
  "director",
  "other",
];

export const PRINT_ONS: PrintOn[] = ["none", "receipt", "invoice", "both"];

/**
 * Field ceilings, mirroring internal/storage/contacts.go. Enforced server-side
 * with a readable 400; duplicated here only so the form can stop a paste before
 * it costs a round-trip. Over-length is a real failure (these strings render
 * onto a public document), not a style preference.
 */
export const MAX_CONTACT_NAME_LEN = 200;
export const MAX_CONTACT_DESIGNATION_LEN = 200;
/** email, phone and notes share one ceiling on the server. */
export const MAX_CONTACT_FREE_TEXT_LEN = 500;

/**
 * Contact — one person attached to a company.
 *
 * Every key is present on every response at its zero value: the DTO carries no
 * `omitempty` on purpose, because a key absent at zero hands this panel
 * `undefined`, and that exact mistake produced a "NaN" in a rate field and a
 * TypeError that blanked the company page (#7 — see the note in
 * src/api/companies.ts). So these are all required here, none optional.
 */
export interface Contact {
  id: string;
  company_id: string;

  name: string;

  /**
   * CLOSED (see ContactRole) — behaviour branches on it, so render it through
   * CONTACT_ROLE_LABELS and pick it from CONTACT_ROLES.
   *
   * Blank on write means "other": the server defaults an empty role rather than
   * rejecting it.
   */
  role: ContactRole;

  /**
   * FREE TEXT, printed VERBATIM onto the document ("Managing Director",
   * "Partner", "Authorised Signatory for GST purposes").
   *
   * DO NOT OFFER A DROPDOWN HERE. Real job titles are open-ended and vary by
   * entity type — a closed set would force operators to misfile people to
   * satisfy a vocabulary go-pim has no business owning, and the misfiled title
   * is the string that ends up on a customer's invoice. The server bounds the
   * length and validates nothing else, by design.
   *
   * Legitimately empty: a contact can print a name with no title under it.
   */
  designation: string;

  email: string;
  phone: string;
  notes: string;

  /** Which documents this person appears on. See PrintOn — role does not. */
  print_on: PrintOn;

  /**
   * Render order within a document, ascending. go-pim's list route orders
   * printing contacts first, then sort_order, then name — so the list arrives
   * already showing what actually reaches a document, without hunting.
   */
  sort_order: number;

  /**
   * Inactive contacts are kept as a record but are EXCLUDED from documents:
   * the render query filters on `active`. This is the soft alternative to a
   * delete for somebody who has left.
   */
  active: boolean;

  /** RFC3339 (UTC). */
  created_at: string;
  updated_at: string;
}

/**
 * Fields accepted by POST /v1/companies/:companyID/contacts and
 * PUT /v1/contacts/:id.
 *
 * company_id is NOT here and is not settable: on create it comes from the path,
 * and on update it is never written — a contact cannot be moved between
 * companies, because the documents it has already been frozen onto belong to the
 * original one.
 *
 * PUT is a FULL-ROW REPLACE of the mutable fields, so always send the complete
 * object — an omitted `active` or `print_on` does not mean "leave it alone", it
 * means false / 'none', and a contact silently dropping off invoices is the kind
 * of thing nobody notices until a customer asks. Costs nothing to comply with:
 * responses carry every key, so anything fetched can be sent straight back.
 *
 * The decoder is STRICT (DisallowUnknownFields) — an unrecognised key is a 400,
 * not a shrug. Never spread UI-only state (a `_dirty` flag, a react-key) into
 * this payload.
 */
export interface ContactWritePayload {
  name: string;
  role: ContactRole;
  designation: string;
  email: string;
  phone: string;
  notes: string;
  print_on: PrintOn;
  sort_order: number;
  active: boolean;
}

/** True when this contact currently reaches a document of the given type. */
export function printsOn(c: Contact, docType: "receipt" | "invoice"): boolean {
  // Mirrors the server's selection exactly (print_on IN (docType,'both') AND
  // active) so a badge in the panel and the actual rendered document can never
  // disagree. Note `active` is part of it: an inactive signatory prints nowhere.
  return c.active && (c.print_on === docType || c.print_on === "both");
}

/**
 * listContacts — GET /v1/companies/:companyID/contacts
 *
 * Returns `{ contacts: [...] }`. Server order is printing-first, then
 * sort_order, then name — preserve it rather than re-sorting by name, since it
 * is the order the operator needs (what reaches a document, at the top).
 */
export async function listContacts(companyId: string): Promise<Contact[]> {
  const resp = await apiClient.get<{ contacts: Contact[] }>(
    `/v1/companies/${companyId}/contacts`,
  );
  return resp.data.contacts ?? [];
}

/** getContact — GET /v1/contacts/:id (flat route; no company segment). */
export async function getContact(id: string): Promise<Contact> {
  const resp = await apiClient.get<Contact>(`/v1/contacts/${id}`);
  return resp.data;
}

/** createContact — POST /v1/companies/:companyID/contacts (201 with the row). */
export async function createContact(
  companyId: string,
  payload: ContactWritePayload,
): Promise<Contact> {
  const resp = await apiClient.post<Contact>(
    `/v1/companies/${companyId}/contacts`,
    payload,
  );
  return resp.data;
}

/**
 * updateContact — PUT /v1/contacts/:id (PUT, not PATCH: full replace).
 *
 * Does NOT retouch any document already issued. The contact was frozen into
 * that document's issuer snapshot at issue time, so a corrected spelling appears
 * on documents issued from now on and on nothing older. If an issued document is
 * wrong, fixing it is a document-level action, not a contact edit.
 */
export async function updateContact(
  id: string,
  payload: ContactWritePayload,
): Promise<Contact> {
  const resp = await apiClient.put<Contact>(`/v1/contacts/${id}`, payload);
  return resp.data;
}

/**
 * deleteContact — DELETE /v1/contacts/:id (204).
 *
 * Documents already issued are UNAFFECTED — the frozen snapshot again — so this
 * cannot make a name vanish from a receipt a customer already holds. The
 * CONSEQUENCE worth confirming is the forward-looking one: the person stops
 * appearing on documents issued from here on, and the row (with its notes,
 * email and phone) is gone for good. When the intent is only "they have left",
 * `active: false` keeps the record and stops the printing just the same.
 */
export async function deleteContact(id: string): Promise<void> {
  await apiClient.delete(`/v1/contacts/${id}`);
}
